import type { MenuEntry, ScreenDefinition } from "./binding";
import { RADIO_COMMUNICATIONS_SCREEN } from "./legacyScreenTitles";

/**
 * Per-operator menu tree used by web + mobile shells.
 */
export const menuTree: MenuEntry[] = [
  {
    id: "nav.operator",
    label: "Operator Overview",
    screenId: "screen.operatorOverview",
    defaultOpen: true,
    children: [
      { id: "nav.emergency", label: "In an Emergency", screenId: "screen.inAnEmergency", navStack: ["screen.operatorOverview", "screen.inAnEmergency"] },
      { id: "nav.company", label: "Company Overview", screenId: "screen.companyOverview", navStack: ["screen.operatorOverview", "screen.companyOverview"] },
      { id: "nav.contacts", label: "Contacts", screenId: "screen.contacts", navStack: ["screen.operatorOverview", "screen.contacts"] },
      {
        id: "nav.assets",
        label: "Assets",
        screenId: "screen.assets",
        navStack: ["screen.operatorOverview", "screen.assets"],
        defaultOpen: false,
        children: [],
      },
      {
        id: "nav.safety",
        label: "Safety",
        defaultOpen: true,
        screenId: "screen.safetyOverview",
        navStack: ["screen.operatorOverview", "screen.safetyOverview"],
        children: [
          {
            id: "nav.safety.ourBranch",
            label: "Our Safety Measures",
            defaultOpen: false,
            screenId: "screen.ourSafetyMeasures",
            navStack: [
              "screen.operatorOverview",
              "screen.safetyOverview",
              "screen.ourSafetyMeasures",
            ],
            children: [
              {
                id: "nav.safety.reliability",
                label: "Reliability",
                screenId: "screen.reliability",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.ourSafetyMeasures",
                  "screen.reliability",
                ],
              },
              {
                id: "nav.safety.prevention",
                label: "Prevention",
                screenId: "screen.preventionMeasures",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.ourSafetyMeasures",
                  "screen.preventionMeasures",
                ],
              },
              {
                id: "nav.safety.construction",
                label: "Construction",
                screenId: "screen.construction",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.ourSafetyMeasures",
                  "screen.construction",
                ],
              },
              {
                id: "nav.safety.maintenance",
                label: "Maintenance",
                screenId: "screen.maintenance",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.ourSafetyMeasures",
                  "screen.maintenance",
                ],
              },
              {
                id: "nav.safety.security",
                label: "Security",
                screenId: "screen.security",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.ourSafetyMeasures",
                  "screen.security",
                ],
              },
              {
                id: "nav.safety.integrity",
                label: "Integrity Management",
                screenId: "screen.integrityManagement",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.ourSafetyMeasures",
                  "screen.integrityManagement",
                ],
              },
            ],
          },
          {
            id: "nav.safety.yourBranch",
            label: "Your Safety & Responsibilities",
            defaultOpen: false,
            screenId: "screen.yourSafetyOverview",
            navStack: [
              "screen.operatorOverview",
              "screen.safetyOverview",
              "screen.yourSafetyOverview",
            ],
            children: [
              {
                id: "nav.safety.lineMarkers",
                label: "Line Markers",
                screenId: "screen.lineMarkers",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.yourSafetyOverview",
                  "screen.lineMarkers",
                ],
              },
              {
                id: "nav.safety.oneCall",
                label: "One Call",
                screenId: "screen.oneCall",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.yourSafetyOverview",
                  "screen.oneCall",
                ],
              },
              {
                id: "nav.safety.encroachment",
                label: "Encroachment",
                screenId: "screen.encroachment",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.yourSafetyOverview",
                  "screen.encroachment",
                ],
              },
              {
                id: "nav.safety.reportDamage",
                label: "Report Damage",
                screenId: "screen.reportDamage",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.yourSafetyOverview",
                  "screen.reportDamage",
                ],
              },
              {
                id: "nav.safety.seeSomething",
                label: "See Something, Say Something",
                screenId: "screen.seeSomething",
                navStack: [
                  "screen.operatorOverview",
                  "screen.safetyOverview",
                  "screen.yourSafetyOverview",
                  "screen.seeSomething",
                ],
              },
            ],
          },
        ],
      },
      { id: "nav.sustainability", label: "Sustainability", screenId: "screen.sustainability", navStack: ["screen.operatorOverview", "screen.sustainability"] },
      { id: "nav.resourcesTop", label: "Resources", screenId: "screen.resources", navStack: ["screen.operatorOverview", "screen.resources"] },
      { id: "nav.makeRequest", label: "Make a Request", screenId: "screen.makeARequest", navStack: ["screen.operatorOverview", "screen.makeARequest"] },
      // `screen.testYourKnowledge` — label matches that screen’s title (see `mergeTestYourKnowledgeMenuLabels`).
      {
        id: "nav.testKnowledge",
        label: "Test Your Knowledge",
        screenId: "screen.testYourKnowledge",
        navStack: ["screen.operatorOverview", "screen.testYourKnowledge"],
      },
    ],
  },
];

/** Same copy as legacy mobile `MAP_DISCLAIMER_TEXT`; shown under map surfaces in web and staticHtml sections. */
export const MAP_PIPELINE_DISCLAIMER_TEXT =
  "Not all pipelines are mapped in Buxus. Map accuracy is determined by our partner operators. For more info, please contact Buxus. Always contact 8-1-1 before digging.";

/** HTML blocks for staticHtml sections until all map pages use the web `MapPipelineDisclaimer` component. */
export const MAP_PIPELINE_DISCLAIMER_HTML = `<p style="opacity:.7;border-top:1px solid #e2e8f0;padding-top:8px;margin-top:12px;font-size:0.75rem;line-height:1.35">${MAP_PIPELINE_DISCLAIMER_TEXT}</p>`;

export const screens: Record<string, ScreenDefinition> = {
  "screen.operatorOverview": {
    id: "screen.operatorOverview",
    title: "Operator Overview",
    titlePath: "operator.operatorName",
    titleFallback: "Operator Overview",
    sections: [
      { kind: "mapPrimary" },
      {
        kind: "optionalDynamicHubLink",
        titlePaths: [
          "operator.emergencyResponderSurveyTitle",
          "operator.emergencyResponderSurveyLinkTitle",
          "operator.surveyTitle",
          "operator.surveyLinkTitle",
        ],
        urlPaths: [
          "operator.emergencyResponderSurveyUrl",
          "operator.emergencyResponderSurveyLink",
          "operator.surveyUrl",
          "operator.surveyLink",
        ],
      },
    ],
  },
  "screen.inAnEmergency": {
    id: "screen.inAnEmergency",
    title: "In an Emergency",
    titlePath: "operator.operatorName",
    titleFallback: "In an Emergency",
    sections: [
      {
        kind: "filteredAssetNav",
        targetScreenId: "screen.assetEmergencyResponse",
      },
    ],
  },
  "screen.assets": {
    id: "screen.assets",
    title: "Assets",
    titlePath: "operator.operatorName",
    titleFallback: "Assets",
    sections: [{ kind: "mapPrimary" }],
  },
  "screen.assetOverview": {
    id: "screen.assetOverview",
    title: "Asset Overview",
    titleFallback: "Asset Overview",
    sections: [{ kind: "mapPrimary" }],
  },
  "screen.assetProfile": {
    id: "screen.assetProfile",
    title: "Asset Profile",
    sections: [{ kind: "assetProfileDetails" }],
  },
  "screen.assetEmergencyResponse": {
    id: "screen.assetEmergencyResponse",
    title: "Emergency Response",
    sections: [{ kind: "assetEmergencyHubNav" }],
  },
  "screen.assetEmergencyReport": {
    id: "screen.assetEmergencyReport",
    title: "Report an Emergency",
    sections: [{ kind: "assetEmergencyReportBody" }],
  },
  "screen.assetEmergencySteps": {
    id: "screen.assetEmergencySteps",
    title: "Steps to Take",
    sections: [{ kind: "assetEmergencyStepsBody" }],
  },
  "screen.assetEmergencyResponder": {
    id: "screen.assetEmergencyResponder",
    title: "Responder Actions",
    sections: [{ kind: "assetEmergencyResponderBody" }],
  },
  "screen.assetEmergencyCompany": {
    id: "screen.assetEmergencyCompany",
    title: "Company Response",
    sections: [{ kind: "assetEmergencyCompanyBody" }],
  },
  "screen.assetEmergencyCommunications": {
    id: "screen.assetEmergencyCommunications",
    title: RADIO_COMMUNICATIONS_SCREEN,
    sections: [{ kind: "assetEmergencyCommunicationsBody" }],
  },
  "screen.assetEmergencyFireSuppression": {
    id: "screen.assetEmergencyFireSuppression",
    title: "Fire Suppression",
    sections: [{ kind: "assetEmergencyFireSuppressionBody" }],
  },
  "screen.assetEmergencyWorstCase": {
    id: "screen.assetEmergencyWorstCase",
    title: "Worst Case Scenarios",
    sections: [{ kind: "assetEmergencyWorstCaseBody" }],
  },
  "screen.assetEmergencyKeyLocations": {
    id: "screen.assetEmergencyKeyLocations",
    title: "Key Locations",
    sections: [{ kind: "assetEmergencyKeyLocationsBody" }],
  },
  "screen.assetLineMarkers": {
    id: "screen.assetLineMarkers",
    title: "Line Markers",
    sections: [
      { kind: "htmlAt", path: "operator.lineMarkers" },
      {
        kind: "imageRefGallery",
        path: "operator.markerImages",
        captionField: "label",
        idField: "id",
        extField: "fileExt",
        markerFilter: "asset",
      },
    ],
  },
  "screen.assetProductsHazards": {
    id: "screen.assetProductsHazards",
    title: "Products & Hazards",
    sections: [{ kind: "productsHazardsCatalog" }],
  },
  "screen.assetHazard": {
    id: "screen.assetHazard",
    title: "Hazard",
    sections: [{ kind: "assetHazardDetailBody" }],
  },
  "screen.assetDocuments": {
    id: "screen.assetDocuments",
    title: "Documents & Plans",
    sections: [{ kind: "assetDocumentsListBody" }],
  },
  "screen.assetKeyLocations": {
    id: "screen.assetKeyLocations",
    title: "Key Locations",
    sections: [{ kind: "assetDirectoryListBody" }],
  },
  "screen.assetGeneralHazards": {
    id: "screen.assetGeneralHazards",
    title: "General Hazards",
    sections: [{ kind: "assetGeneralHazardsLegacyBody" }],
  },
  "screen.assetSpecialNotes": {
    id: "screen.assetSpecialNotes",
    title: "Special Notes",
    sections: [{ kind: "assetSpecialNotesLegacyBody" }],
  },
  "screen.companyOverview": {
    id: "screen.companyOverview",
    title: "Company Overview",
    titlePath: "operator.operatorName",
    titleFallback: "Company Overview",
    sections: [
      { kind: "primaryCorporateContact", label: "Corporate Contact" },
      { kind: "companyOverviewDetails" },
      { kind: "labeledHtmlAt", heading: "Corporate Overview", path: "operator.corporateOverview" },
      { kind: "labeledStakeholderRows", heading: "Pipeline Purpose", path: "operator.pipelinePurposeSubform", mergeRows: true },
      { kind: "htmlAt", path: "operator.privacyPolicy" },
      { kind: "optionalCta", heading: "Privacy Policy", label: "View Privacy Policy", urlPath: "operator.privacyLink" },
    ],
  },
  "screen.contacts": {
    id: "screen.contacts",
    title: "Contacts",
    titlePath: "operator.operatorName",
    titleFallback: "Contacts",
    sections: [{ kind: "contactDirectory", path: "operator.contacts" }],
  },
  "screen.makeARequest": {
    id: "screen.makeARequest",
    title: "Make a Request",
    titlePath: "operator.operatorName",
    titleFallback: "Make a Request",
    sections: [{ kind: "makeARequestHub" }],
  },
  "screen.sustainability": {
    id: "screen.sustainability",
    title: "Sustainability",
    titlePath: "operator.operatorName",
    titleFallback: "Sustainability",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.sustainabilitySubform", mergeRows: true }],
  },
  /** List screen for `operator.testYourKnowledge`; `title` is this screen’s name (row `linkTitle` appears in body only). */
  "screen.testYourKnowledge": {
    id: "screen.testYourKnowledge",
    title: "Test Your Knowledge",
    titlePath: "operator.operatorName",
    titleFallback: "Test Your Knowledge",
    sections: [{ kind: "tykLinks", path: "operator.testYourKnowledge" }],
  },
  "screen.safetyOverview": {
    id: "screen.safetyOverview",
    title: "Safety Overview",
    sections: [
      {
        kind: "branchHub",
        items: [
          { label: "Our Safety Measures", targetScreenId: "screen.ourSafetyMeasures" },
          { label: "Your Safety & Responsibilities", targetScreenId: "screen.yourSafetyOverview" },
        ],
      },
    ],
  },
  "screen.ourSafetyMeasures": {
    id: "screen.ourSafetyMeasures",
    title: "Our Safety Measures",
    sections: [
      {
        kind: "branchHub",
        intro: { path: "operator.safetyOverviewSubform", mergeRows: true },
        items: [
          { label: "Reliability", targetScreenId: "screen.reliability" },
          { label: "Prevention", targetScreenId: "screen.preventionMeasures" },
          { label: "Construction", targetScreenId: "screen.construction" },
          { label: "Maintenance", targetScreenId: "screen.maintenance" },
          { label: "Security", targetScreenId: "screen.security" },
          { label: "Integrity Management", targetScreenId: "screen.integrityManagement" },
        ],
      },
    ],
  },
  "screen.integrityManagement": {
    id: "screen.integrityManagement",
    title: "Integrity Management",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.integrityManagementSubform", mergeRows: true }],
  },
  "screen.reliability": {
    id: "screen.reliability",
    title: "Reliability",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.reliabilitySubform", mergeRows: true }],
  },
  "screen.preventionMeasures": {
    id: "screen.preventionMeasures",
    title: "Prevention",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.preventionSubform", mergeRows: true }],
  },
  "screen.construction": {
    id: "screen.construction",
    title: "Construction",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.constructionSubform", mergeRows: true }],
  },
  "screen.maintenance": {
    id: "screen.maintenance",
    title: "Maintenance",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.maintenanceSubform", mergeRows: true }],
  },
  "screen.security": {
    id: "screen.security",
    title: "Security",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.securitySubform", mergeRows: true }],
  },
  "screen.yourSafetyOverview": {
    id: "screen.yourSafetyOverview",
    title: "Your Safety & Responsibilities",
    sections: [
      {
        kind: "branchHub",
        intro: { path: "operator.damagePreventionOverview", mergeRows: true },
        items: [
          { label: "Line Markers", targetScreenId: "screen.lineMarkers" },
          { label: "One Call", targetScreenId: "screen.oneCall" },
          { label: "Encroachment", targetScreenId: "screen.encroachment" },
          { label: "Report Damage", targetScreenId: "screen.reportDamage" },
          { label: "See Something, Say Something", targetScreenId: "screen.seeSomething" },
        ],
      },
    ],
  },
  "screen.lineMarkers": {
    id: "screen.lineMarkers",
    title: "Line Markers",
    sections: [
      { kind: "htmlAt", path: "operator.lineMarkers" },
      {
        kind: "imageRefGallery",
        path: "operator.markerImages",
        captionField: "label",
        idField: "id",
        extField: "fileExt",
        markerFilter: "operator",
      },
    ],
  },
  "screen.oneCall": {
    id: "screen.oneCall",
    title: "One Call",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.oneCall", mergeRows: true }],
  },
  "screen.encroachment": {
    id: "screen.encroachment",
    title: "Encroachment",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.encroachment", mergeRows: true }],
  },
  "screen.reportDamage": {
    id: "screen.reportDamage",
    title: "Report Damage",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.reportDamage", mergeRows: true }],
  },
  "screen.seeSomething": {
    id: "screen.seeSomething",
    title: "See Something, Say Something",
    sections: [{ kind: "htmlStakeholderRows", path: "operator.seeSomethingSaySomething", mergeRows: true }],
  },
  "screen.resources": {
    id: "screen.resources",
    title: "Resources",
    sections: [{ kind: "resourcesScreenLegacy" }],
  },
};
